import * as React from 'react';
import { useLocation } from 'react-router-dom';
import * as _ from 'underscore';
import { GameStateDB } from '../../../data/datatypes';
import { getGameState, saveGameState } from '../../../services/DBService/DbService';
import { useAutoSaveOnPageLeave } from '../../../store/hooks';
import Image from '../PageElements/Image/Image';
import { ImageElement } from '@app/common';
import TextWithReferences from '../PageElements/TextWithReferences/TextWithReferences';
import { getUrlWithTransientStateProperties } from '../Sessions/Session.logic';
import { GameSelectItemsData, GameSelectItemsState } from '../Games.types';

type Properties = {
    data: GameSelectItemsData;
    setNextButtonLabel: (label: string | undefined) => void;
};

const SelectItems = (props: Properties) => {
    const { caption, desc } = props.data;

    const [step, setStep] = React.useState<'playing' | 'result'>('playing');
    const [selected, setSelected] = React.useState<number[]>([]);
    const [rndItems, setRndItems] = React.useState<(GameSelectItemsData['items'][0] & { index: number })[]>(
        _.shuffle(props.data.items.map((item, index) => { return { ...item, index }; }))
    );

    const path = useLocation().pathname;
    const dbId: `game_${string}` = `game_${getUrlWithTransientStateProperties(path)}`;

    useAutoSaveOnPageLeave(
        [step, selected, rndItems],
        async () => {
            const gameState: GameStateDB = {
                _id: dbId,
                state: {
                    step,
                    selected,
                    rndItems
                }
            };
            await saveGameState(gameState);
        },
        [path],
        async () => {
            const state = await getGameState(dbId);
            if (state) {
                setStep((state.state as GameSelectItemsState).step);
                setSelected((state.state as GameSelectItemsState).selected);
                setRndItems((state.state as GameSelectItemsState).rndItems);
            }
        }
    );

    // set next-Button label
    React.useEffect(() => {
        props.setNextButtonLabel('Spiel beenden');
        return () => {
            props.setNextButtonLabel(undefined);
        };
    }, [props.setNextButtonLabel]);

    const itemClicked = (value: number) => {
        let newSelected = [...selected];
        let selInd = _.findIndex(newSelected, i => i === value);
        if (selInd > -1) {
            newSelected.splice(selInd, 1);
        } else {
            newSelected.push(value);
        }
        setSelected(newSelected);
    };

    const showResult = () => {
        setStep('result');
    };

    const reset = () => {
        setRndItems(_.shuffle(props.data.items.map((item, index) => { return { ...item, index }; })));
        setStep('playing');
        setSelected([]);
    };

    const itemEls: JSX.Element[] = rndItems.map((item, index) => {
        let isSelected = selected.indexOf(item.index) !== -1;
        // let correct = (selected && item.correct === true) || (!selected && item.correct === false);

        let className = 'select-items-item select-items-item-' + (isSelected === true ? 'selected' : 'unselected');
        if (step === 'result') {
            className += ' select-items-item-' + (item.correct === true ? 'correct' : 'incorrect');
        }

        let onClick = () => {
            itemClicked(item.index);
        };
        if (step === 'result') {
            onClick = () => { return; };
        }

        switch (item.type) {
            case 'text':
                return (
                    <div
                        className={className + ' '}
                        onClick={onClick}
                        key={index}
                    >
                        <div className="select-items-text">{item.text}</div>
                    </div>
                );

            case 'image':
                const imageData: ImageElement = {
                    type: 'image',
                    id: item.id
                };
                return (
                    <div
                        className={className + ' select-items-item-image'}
                        onClick={onClick}
                        key={index}
                    >
                        <Image data={imageData} className="select-items-image" />
                    </div>
                );

            default:
                return <></>;
        }
    });

    let btn = <button onClick={showResult} className="primary">Ergebnis anzeigen</button>;
    if (step === 'result') {
        btn = <button onClick={reset} className="primary">Mach's noch mal</button>;
    }

    return (
        <div className="game">
            <div className="game-content">
                <div className="game-select-items">
                    <h1>{caption}</h1>
                    <p><TextWithReferences text={desc || ''} /></p>
                    <div className="game-select-items-container">
                        {itemEls}
                    </div>
                </div>
            </div>
            <div className="game-menu">
                {btn}
            </div>
        </div>
    );
};

export default SelectItems;